// src/pages/dashboard/branch/categories/components/UpdateCategoryModal.tsx
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiEye, FiEyeOff, FiLayers, FiUpload, FiX } from 'react-icons/fi';
import Button from '../../../../../components/ui/Button';
import InputField from '../../../../../components/ui/InputField';
import Toggle from '../../../../../components/ui/Toggle';
import { getCategoryName } from '../../../../../utils/categoryHelper';
import { Category } from '../../../../../utils/supabaseClient';
import { ArTranslation } from '../../../../../types/custom';
import { uploadToSupabaseStorage } from '../../../../../utils/supabaseStorage';

import { useAppDispatch } from '../../../../../store/hooks';
import { updateCategoryData } from '../../../../../store/slices/categorySlice';

interface UpdateCategoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCategoryUpdated: (category: Category) => void;
	category: Category;
	parentCategories: Category[];
	branchId: string;
}

const UpdateCategoryModal: React.FC<UpdateCategoryModalProps> = ({
	isOpen,
	onClose,
	onCategoryUpdated,
	category,
	parentCategories,
	branchId
}) => {
	const { t, i18n } = useTranslation();
	const isRTL = i18n.language === 'ar';

	const dispatch = useAppDispatch()

	const [formData, setFormData] = useState({
		name: '',
		description: '',
		arName: '',
		arDescription: '',
		parentId: '',
		isPublished: true
	});

	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [imageChanged, setImageChanged] = useState(false);
	const [imageRemoved, setImageRemoved] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Initialize form data when category changes
	useEffect(() => {
		if (category) {
			const ar = category.ar as ArTranslation | null;
			setFormData({
				name: category.name || '',
				description: category.description || '',
				arName: ar?.name || '',
				arDescription: ar?.description || '',
				parentId: category.parent_id || '',
				isPublished: category.is_published ?? true
			});
			setImagePreview(category.image_url || null);
			setImageChanged(false);
			setImageRemoved(false);
			setError(null);
		}
	}, [category]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			setImageFile(file);
			setImageChanged(true);
			setImageRemoved(false);

			const reader = new FileReader();
			reader.onloadend = () => {
				setImagePreview(reader.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const removeImage = () => {
		setImageFile(null);
		setImagePreview(null);
		setImageChanged(true);
		setImageRemoved(true);
	};

	const validateForm = () => {
		const errors: string[] = [];

		if (!formData.name.trim()) {
			errors.push(t('categories.errors.nameRequired'));
		}

		if (i18n.language === 'ar' && !formData.arName.trim()) {
			errors.push(t('categories.errors.arNameRequired'));
		}

		return errors;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const validationErrors = validateForm();
		if (validationErrors.length > 0) {
			setError(validationErrors[0]);
			return;
		}

		setIsLoading(true);

		try {
			// Handle image upload if changed
			let imageUrl = category.image_url;

			if (imageChanged) {
				if (imageRemoved) {
					imageUrl = null;
				} else if (imageFile) {
					const uniqueFileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${imageFile.name}`;
					const uploadResult = await uploadToSupabaseStorage(imageFile, uniqueFileName, {
						bucket: 'uploads',
						path: `categories/${branchId}`
					});
					imageUrl = uploadResult.publicUrl;
				}
			}

			const categoryData = {
				id: category.id,
				name: formData.name,
				description: formData.description,
				image_url: imageUrl,
				parent_id: formData.parentId || null,
				is_published: formData.isPublished,
				ar: {
					name: formData.arName || null,
					description: formData.arDescription || null
				},
			};

			// const result = await updateCategory(categoryData);
			// if (!result) return

			// if (!result.success) console.log(result.message);

			// onCategoryUpdated(result.data);

			const result = await dispatch(updateCategoryData(categoryData)).unwrap();
			// Redux already updated state, notify parent
			onCategoryUpdated(result);
			handleClose();
		} catch (err) {
			console.error('Error updating category:', err);
			setError(t('categories.errors.updateFailed'));
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = () => {
		setImageFile(null);
		setImageChanged(false);
		setImageRemoved(false);
		setError(null);
		onClose();
	};

	const modalVariants = {
		hidden: { opacity: 0, scale: 0.95 },
		visible: { opacity: 1, scale: 1 },
		exit: { opacity: 0, scale: 0.95 }
	};

	const overlayVariants = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
		exit: { opacity: 0 }
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
						<motion.div
							variants={overlayVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							className="fixed inset-0 bg-black/50"
							onClick={handleClose}
						/>

						<motion.div
							variants={modalVariants}
							initial="hidden"
							animate="visible"
							exit="exit"
							transition={{ type: "spring", duration: 0.3 }}
							className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-start align-middle bg-white dark:bg-gray-800 rounded-lg shadow-xl relative z-10"
							onClick={(e) => e.stopPropagation()}
						>
							{/* Header */}
							<div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
										<FiLayers className="w-5 h-5 text-primary" />
									</div>
									<h3 className="text-lg font-medium text-gray-900 dark:text-white">
										{t('categories.editCategory')}
									</h3>
								</div>
								<button
									onClick={handleClose}
									className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
								>
									<FiX size={20} />
								</button>
							</div>

							{/* Form */}
							<form onSubmit={handleSubmit} className="mt-6">
								{error && (
									<motion.div
										initial={{ opacity: 0, y: -10 }}
										animate={{ opacity: 1, y: 0 }}
										className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-sm"
									>
										{error}
									</motion.div>
								)}

								<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
									{/* Left Column - Basic Info */}
									<div className="flex flex-col gap-4">
										<h4 className="text-md font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
											{t('categories.basicInformation')}
										</h4>

										{/* Category Name */}
										<InputField
											label={t('categories.enName')}
											name="name"
											value={formData.name}
											onChange={handleInputChange}
											placeholder={t('categories.namePlaceholder')}
											required
										/>

										{/* Description */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
												{t('categories.enDescription')}
											</label>
											<textarea
												name="description"
												value={formData.description}
												onChange={handleInputChange}
												placeholder={t('categories.descriptionPlaceholder')}
												rows={3}
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
											/>
										</div>

										{/* Parent Category */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
												{t('categories.parentCategory')}
											</label>
											<select
												name="parentId"
												value={formData.parentId}
												onChange={handleInputChange}
												className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
											>
												<option value="">{t('categories.noParent')}</option>
												{parentCategories
													.filter(cat => cat.id !== category.id) // Don't allow selecting itself as parent
													.map((parentCat) => (
														<option key={parentCat.id} value={parentCat.id}>
															{getCategoryName(parentCat, i18n.language)}
														</option>
													))}
											</select>
											{/* <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
												{t('categories.parentCategoryHelper')}
											</p> */}
										</div>

										{/* Arabic Translations */}
										<div className="border-t border-gray-200 dark:border-gray-700 pt-4">
											<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
												{t('categories.arabicTranslation')}
											</h5>

											<div className="flex flex-col gap-3">
												<InputField
													label={t('categories.arName')}
													name="arName"
													value={formData.arName}
													onChange={handleInputChange}
													placeholder={t('categories.arNamePlaceholder')}
													dir="rtl"
												/>

												<div>
													<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
														{t('categories.arDescription')}
													</label>
													<textarea
														name="arDescription"
														value={formData.arDescription}
														onChange={handleInputChange}
														placeholder={t('categories.arDescriptionPlaceholder')}
														rows={3}
														dir="rtl"
														className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
													/>
												</div>
											</div>
										</div>
									</div>

									{/* Right Column - Image & Settings */}
									<div className="flex flex-col gap-4">
										<h4 className="text-md font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
											{t('categories.imageAndSettings')}
										</h4>

										{/* Image Upload */}
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
												{t('categories.image')}
											</label>

											{!imagePreview ? (
												<label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
													<div className="flex flex-col items-center justify-center pt-5 pb-6">
														<FiUpload className="w-8 h-8 mb-2 text-gray-400" />
														<p className="text-sm text-gray-500 dark:text-gray-400 text-center">
															{t('categories.uploadImage')}
														</p>
													</div>
													<input
														type="file"
														accept="image/*"
														onChange={handleImageChange}
														className="hidden"
													/>
												</label>
											) : (
												<div className="relative">
													<img
														src={imagePreview}
														alt="Preview"
														className="w-full h-40 object-cover rounded-lg"
													/>
													<button
														type="button"
														onClick={removeImage}
														className="absolute top-2 end-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors cursor-pointer"
													>
														<FiX size={16} />
													</button>
													<label className="absolute bottom-2 start-2 p-2 bg-black/50 text-white rounded-md cursor-pointer hover:bg-black/70 transition-colors">
														<FiUpload size={16} />
														<input
															type="file"
															accept="image/*"
															onChange={handleImageChange}
															className="hidden"
														/>
													</label>
												</div>
											)}
										</div>

										{/* Settings */}
										<div className="flex flex-col gap-4">
											<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
												{t('categories.settings')}
											</h5>

											{/* Published Toggle */}
											<div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
												<div className="flex items-center gap-3">
													{formData.isPublished ? (
														<FiEye className="w-5 h-5 text-green-500" />
													) : (
														<FiEyeOff className="w-5 h-5 text-gray-400" />
													)}
													<div>
														<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
															{t('categories.published')}
														</span>
														<p className="text-xs text-gray-500 dark:text-gray-400">
															{formData.isPublished
																? t('categories.publishedHelper')
																: t('categories.unpublishedHelper')
															}
														</p>
													</div>
												</div>
												<Toggle
													enabled={formData.isPublished}
													onChange={(enabled) => setFormData(prev => ({ ...prev, isPublished: enabled }))}
												/>
											</div>

											{/* Category Type Info */}
											<div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
												<div className="flex items-start gap-3">
													<FiLayers className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
													<div>
														<p className="text-sm font-medium text-blue-800 dark:text-blue-300">
															{formData.parentId
																? t('categories.subcategoryType')
																: t('categories.mainCategoryType')
															}
														</p>
														<p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
															{formData.parentId
																? t('categories.subcategoryHelper')
																: t('categories.mainCategoryHelper')
															}
														</p>
													</div>
												</div>
											</div>
										</div>

										{/* Category Stats */}
										<div className="border-t border-gray-200 dark:border-gray-700 pt-4">
											<h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
												{t('categories.categoryStats')}
											</h5>
											<div className="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400">
												<div className="flex justify-between">
													<span>{t('categories.createdAt')}:</span>
													<span>{new Date(category.created_at).toLocaleDateString(i18n.language)}</span>
												</div>
												<div className="flex justify-between">
													<span>{t('categories.lastUpdated')}:</span>
													<span>{new Date(category.updated_at || category.created_at).toLocaleDateString(i18n.language)}</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Actions */}
								<div className="flex gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
									<Button
										type="button"
										variant="outline"
										onClick={handleClose}
										disabled={isLoading}
										className="flex-1"
									>
										{t('common.cancel')}
									</Button>
									<Button
										type="submit"
										isLoading={isLoading}
										disabled={!formData.name.trim()}
										className="flex-1"
										rightIcon={isRTL ? <FiChevronLeft /> : <FiChevronRight />}
									>
										{t('categories.updateCategory')}
									</Button>
								</div>
							</form>
						</motion.div>
					</div>
				</div>
			)}
		</AnimatePresence>
	);
};

export default UpdateCategoryModal;